import React, { useEffect, useState } from 'react';
import {
  Box,
  Heading,
  Text,
  Button,
  useToast,
  Card,
  CardBody,
  VStack,
  Flex,
  Select,
  HStack,
  IconButton,
  Spinner,
} from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';

import { HiClipboard } from 'react-icons/hi';
import { FiAlertCircle } from 'react-icons/fi';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import { BlueCheckApplication } from '../../utils/types';
import { OAuthNewCodeRender } from './component/bluecheck-code-render.component';
import { getApplicationById } from '../../utils/bluecheck-applications/fetch-bluecheck-applications';

export const IntegrationDetailsScreen: React.FC<{
  createdApp: BlueCheckApplication | null;
}> = ({ createdApp }) => {
  const { appId } = useParams();
  const [application, setApplication] = useState<BlueCheckApplication | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (appId) {
      const fetchAppbyId = async () => {
        setLoading(true);
        const data = await getApplicationById(appId as string);
        setLoading(false);
        setApplication(data);
        setEditMode(true);
      };
      fetchAppbyId();
    }
  }, []);

  const navigate = useNavigate();
  const toast = useToast();
  const { width } = useWindowDimensions();
  const [selectedLanguage, setSelectedLanguage] = useState('react');

  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedLanguage(event.target.value);
  };
  const handleCopy = (value: string | null) => {
    if (!value) return;
    navigator.clipboard
      .writeText(value)
      .then(() => {
        toast({
          title: 'Copied to Clipboard',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      })
      .catch((err) => {
        console.error('Failed to copy: ', err);
        toast({
          title: 'Copy Failed',
          description: 'Unable to copy the App ID.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      });
  };
  if (loading) {
    return (
      <Box textAlign="center" mt={6}>
        <Spinner size="lg" />
        <Text mt={4}>Loading Application...</Text>
      </Box>
    );
  }
  return (
    <Box display="flex" h="fit-content" justifyContent="center" width="100%">
      <Card
        w={width > 1740 ? '1300px' : '95%'}
        variant="outline"
        mt={6}
        backgroundColor="#FFF"
        borderRadius="12px"
        h="fit-content"
      >
        <CardBody p={0} justifyContent="space-between">
          <VStack paddingX="32px" paddingTop="32px" h={'72vh'} overflowY="scroll">
            <VStack align="flex-start" w="100%" spacing="2px">
              <Text
                fontFamily="inter"
                className="medium-font"
                fontWeight="bold"
                color="black"
              >
                Copy Paste Code
              </Text>
              <Text
                color="gray"
                className="small-font"
                whiteSpace="pre-line"
                fontFamily="inter"
              >
                Follow the Steps below to integrate in your application
              </Text>
            </VStack>
            <HStack flexWrap="wrap" mt="16px" w="100%" spacing="32px">
              <VStack alignItems="flex-start" w="35%" spacing={0}>
                <HStack>
                  <Text
                    color="gray"
                    className="small-font"
                    whiteSpace="pre-line"
                    wordBreak="break-word"
                    noOfLines={1}
                    fontFamily="inter"
                  >
                    Application ID
                  </Text>
                  <IconButton
                    size="sm"
                    icon={<HiClipboard />}
                    onClick={() =>
                      handleCopy(
                        createdApp
                          ? (createdApp?.id as string)
                          : (application?.id as string)
                      )
                    }
                    aria-label="Copy App ID"
                  />
                </HStack>
                <Text
                  color="black.600"
                  className="small-font"
                  fontWeight="bold"
                  w="100%"
                  noOfLines={1}
                  whiteSpace="pre-line"
                  fontFamily="inter"
                >
                  {application?.id || createdApp?.id}
                </Text>
              </VStack>
              <VStack alignItems="flex-start" w="35%" spacing="0px">
                <HStack>
                  <Text
                    color="gray"
                    className="small-font"
                    whiteSpace="pre-line"
                    wordBreak="break-word"
                    noOfLines={1}
                    fontFamily="inter"
                  >
                    Application Secret
                  </Text>
                  <IconButton
                    size="sm"
                    cursor={createdApp ? 'pointer' : 'not-allowed'}
                    icon={<HiClipboard />}
                    onClick={() =>
                      handleCopy(createdApp ? createdApp.clientSecret : null)
                    }
                    aria-label="Copy Secret Key"
                  />
                </HStack>
                <Text
                  color="black.600"
                  className="small-font"
                  w="100%"
                  whiteSpace="pre-line"
                  fontWeight="bold"
                  noOfLines={1}
                  fontFamily="inter"
                >
                  {createdApp
                    ? createdApp.clientSecret
                    : '**************************************'}
                </Text>
              </VStack>

              <VStack alignItems="flex-start" w="20%" spacing="0px">
                <Text
                  color="gray"
                  className="small-font"
                  whiteSpace="pre-line"
                  wordBreak="break-word"
                  noOfLines={1}
                  fontFamily="inter"
                >
                  Packages
                </Text>
                <Select
                  value={selectedLanguage}
                  onChange={handleChange}
                  opacity="1"
                  fontSize={width < 1368 ? '12px' : '14px'}
                  bg="gray.100"
                >
                  <option value="react">ReactJs</option>
                  {/* <option value="js">JavaScript</option> */}
                </Select>
              </VStack>
            </HStack>
            {createdApp && (
              <Flex
                bgColor="blue.50"
                px={5}
                mt="16px"
                w="100%"
                py={4}
                gap={3}
                borderRadius={8}
                borderLeft="4px solid"
                borderColor="blue.400"
                boxShadow="sm"
                alignItems="flex-start"
              >
                <Box pt="3px" mt={1}>
                  <FiAlertCircle fontSize="1.3rem" color="blue.500" />
                </Box>

                <Flex direction="column">
                  <Heading
                    className="small-font"
                    fontFamily="inter"
                    fontWeight={600}
                    color="blue.700"
                  >
                    Make sure you copy the Application Secret
                  </Heading>
                  <Text className="small-font" fontFamily="inter" color="blue.600">
                    Once you leave this page, you will not be able to see the Application
                    Secret again
                  </Text>
                </Flex>
              </Flex>
            )}
            <VStack w="100%" mt="16px" spacing="0px">
              <VStack w="100%" align="flex-start" spacing="2px">
                <OAuthNewCodeRender
                  language={selectedLanguage}
                  application={createdApp ? createdApp : application}
                  editMode={editMode}
                />
              </VStack>
            </VStack>
            <Flex alignItems="right" gap="17px">
              <Button
                onClick={() => {
                  navigate('/my-applications', { replace: true });
                }}
                className="small-font stepper-next-button"
                fontFamily="inter"
                mb={'20px'}
                _hover={{
                  transform: 'scale(1.05)',
                }}
              >
                Back to your Applications
              </Button>
            </Flex>
          </VStack>
        </CardBody>
      </Card>
    </Box>
  );
};
